import { useState, useEffect } from 'react';
import { Download, Wifi, WifiOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('pwa_install_dismissed')) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  if (!showPrompt || dismissed) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 bg-foreground text-white rounded-2xl p-4 shadow-2xl border border-stone-700 animate-in slide-in-from-bottom-4" data-testid="pwa-install-prompt">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center shrink-0">
          <Download size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">Install Smarto</p>
          <p className="text-xs text-muted-foreground mt-0.5">Add to home screen for faster access and offline support</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="rounded-full bg-orange-500 hover:bg-orange-600 text-xs h-7" onClick={handleInstall} data-testid="pwa-install-btn">Install</Button>
            <Button size="sm" variant="ghost" className="rounded-full text-xs h-7 text-muted-foreground" onClick={handleDismiss}>Not now</Button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-muted-foreground hover:text-white"><X size={16} /></button>
      </div>
    </div>
  );
}

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-warning text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-2" data-testid="offline-banner">
      <WifiOff size={14} />
      You are offline. Some features may be limited.
    </div>
  );
}
